import React, { useEffect, useState } from "react"
import { Control } from "react-hook-form"
import { FormProvider } from "~/contexts/FormContext"
import useAdminForm from "~/hooks/useAdminForm"
import Form from "./index"
import api from "~/api2"
import { snakeCase } from "lodash"
import { useMe } from "~/api2/hooks/useMe"
import { useDebouncedCallback } from "use-debounce"
import Button from "~/components/ui/Button"
import { TrashIcon } from "@heroicons/react/24/solid"
import { useConfirm } from "~/hooks/useConfirm"
import { useNotification } from "~/contexts/NotificationProvider"
import { useModal } from "~/contexts/ModalProvider"
import { Disclosure } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/24/solid'

type FieldMeta = { name: string; type: string }

interface Props<T extends Record<string, unknown>> {
  campId: string
  model: string
  init: T
  onSaved?: (record: T & { id: string }) => void
  fields?: FieldMeta[]
  isInline?: boolean
  hideDelete?: boolean
  parentModel?: string
  onDuplicate?: () => void
}

function InputFor(parentModelNameSnakeCase: string, field: FieldMeta, control: Control<Record<string, unknown>>) {
  let props = { field: field.name, label: field.name, control }

  if (
    field.name == "id" ||
    field.name.includes("camp_id") ||
    field.name == `${parentModelNameSnakeCase}_id` ||
    field.name.includes("created_at") ||
    field.name.includes("updated_at")
  ) {
    props["disabled"] = true
    props["hidden"] = true
    props["className"] = "hidden"
  }

  switch (field.type) {
    case "integer":
      return <Form.NumberInput key={field.name} {...props} />
    case "decimal":
      return <Form.DecimalInput key={field.name} {...props} />
    case "boolean":
      return <Form.CheckboxInput key={field.name} field={field.name} label={field.name} />
    default:
      return <Form.TextInput key={field.name} {...props} />
  }
}

const Card: React.FC = ({ children }) => (
  <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">{children}</div>
)

function InternalForm({
  fields,
  onSubmit,
  isInline = false,
  showDelete,
  onDelete,
  onDuplicate,
  parentModelNameSnakeCase,
}: {
  fields: FieldMeta[]
  onSubmit: (d: Record<string, unknown>) => Promise<void>
  isInline?: boolean
  showDelete?: boolean
  onDelete?: () => void
  onDuplicate?: () => void
  parentModelNameSnakeCase: string
}) {
  const { handleSubmit, control } = useAdminForm<Record<string, unknown>>()
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 flex flex-col w-full">
      <div className="sticky bottom-0 left-0 top-20 z-10 bg-white pt-3 pb-1 flex gap-2  w-full">
        <Button type="submit" variant="green" size="small" className="ml-auto">
          Save
        </Button>
        {onDuplicate && (
          <Button type="button" variant="gray" size="small" onClick={onDuplicate}>
            Duplicate
          </Button>
        )}
        {showDelete && (
          <Button
            type="button"
            variant="red"
            size="small"
            onClick={onDelete}
            icon={<TrashIcon className="w-5 h-5" />}
          />
        )}
      </div>
      <div className={isInline ? "grid grid-cols-4 gap-4" : "grid grid-cols-2 gap-6 space-y-2"}>
        {fields.map((f) => InputFor(parentModelNameSnakeCase, f, control))}
      </div>
    </form>
  )
}

function ResourceDynamicFormContent<T extends Record<string, unknown>>({
  campId,
  model,
  init,
  onSaved,
  fields,
  isInline,
  hideDelete = false,
  parentModel,
  onDuplicate,
}: Props<T>) {
  const { reset, updateDefaults } = useAdminForm<Record<string, unknown>>()
  const { showConfirm, ConfirmModal } = useConfirm()
  const { showNotification } = useNotification()
  const id = init?.id as string

  const handleSubmit = async (data: Record<string, unknown>) => {
    let res: { record: T & { id: string } }

    const cleaned = { ...data }
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === "") delete cleaned[key]
    })

    try {
      if (id) {
        res = await api.DynamicForms.update(model, campId, id, cleaned)
      } else {
        res = await api.DynamicForms.create(model, campId, cleaned)
      }

      const changed = Object.keys(res.record).filter((k) => init[k] !== res.record[k])
      showNotification({
        type: "success",
        title: "Changes saved",
        description: changed.length ? `${changed.join(", ")} updated` : undefined,
        dismissAfter: 3000,
      })

      updateDefaults(res.record as Record<string, unknown>)
      if (!id) reset(init)

      if (onSaved) onSaved(res.record)
    } catch (err: any) {
      showNotification({ type: "error", title: "Save failed", description: err.message })
    }
  }

  const handleDelete = async () => {
    if (!id) return
    const first = await showConfirm({
      title: "Delete this record?",
      description: "This action cannot be undone.",
      ctaLabel: "Delete",
      colorName: "red",
      ctaColorName: "red",
    })

    if (!first) return

    try {
      await api.DynamicForms.remove(model, campId, id)
      showNotification({ type: "success", title: "Record removed" })
      if (onSaved) onSaved(init as T & { id: string })
    } catch (err: any) {
      showNotification({ type: "error", title: "Delete failed", description: err.message })
    }
  }

  return (
    <>
      <InternalForm
        fields={fields}
        onSubmit={handleSubmit}
        isInline={isInline}
        showDelete={!!id && !hideDelete && Object.keys(init).includes("deleted_at")}
        onDelete={handleDelete}
        onDuplicate={onDuplicate}
        parentModelNameSnakeCase={snakeCase(parentModel)}
      />
      <ConfirmModal />
    </>
  )
}

function ResourceDynamicForm<T extends Record<string, unknown>>(props: Props<T>) {
  // Inline forms (e.g., within sub-resources) are not accordions
  if (props.isInline) {
    return (
      <FormProvider init={props.init} removeWarnOnNavigate={false}>
        <Card>
          <ResourceDynamicFormContent {...props} />
        </Card>
      </FormProvider>
    )
  }
  const title = `${props.model}${props.init?.id ? ` #${props.init.id}` : ''}`
  // Parent forms render as accordion, defaultOpen to always show
  return (
    <Disclosure as="div" defaultOpen className="mb-4">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left bg-gray-100 rounded-lg hover:bg-gray-200">
            <span>{title}</span>
            <ChevronUpIcon className={`${open ? 'transform rotate-180' : ''} w-5 h-5`} />
          </Disclosure.Button>
          <Disclosure.Panel className="mt-2">
            <FormProvider init={props.init} removeWarnOnNavigate={false}>
              <Card>
                <ResourceDynamicFormContent {...props} />
              </Card>
            </FormProvider>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}

function DynamicForm<T extends Record<string, unknown>>({ model, init, id, onSaved }: Props<T>) {
  const {
    data: { camp_id: campId },
  } = useMe()
  const { openModal, closeModal } = useModal()
  const [fields, setFields] = useState<FieldMeta[]>([])
  const [subResources, setSubResources] = useState<Record<string, unknown>[]>([])

  useEffect(() => {
    refetchResource(model)
  }, [model])

  const refetchResource = useDebouncedCallback(async (modelName: string) => {
    return api.DynamicForms.metadata(modelName).then((data) => {
      setFields(data.fields)
      setSubResources(data.sub_resources || [])
    })
  }, 100)

  return (
    <div>
      <ResourceDynamicForm
        campId={campId}
        model={model}
        init={init}
        onSaved={(record) => {
          if (onSaved) onSaved(record)
          refetchResource(model)
        }}
        fields={fields}
        hideDelete
      />
      {subResources.map((subResource) => {
        const subResourceKey = snakeCase(subResource.model) + "s"
        const initData = init[subResourceKey] || ([] as never[])
        const title = `${subResource.model}s`
        const handleShowSubResourceModal = () => {
          openModal("generic", {
            children: (
              <ResourceDynamicForm
                campId={campId}
                model={subResource.model}
                init={{ [`${snakeCase(model)}_id`]: init["id"] } as Record<string, unknown>}
                onSaved={(record) => {
                  const updatedData = [...initData, record]
                  onSaved({ ...init, [subResourceKey]: updatedData })
                  refetchResource(model)
                  closeModal()
                }}
                fields={subResource.fields}
                parentModel={model}
              />
            ),
            onClose: () => {
              closeModal()
            },
            className: "max-w-2xl w-full",
          })
        }
        return (
          <Disclosure as="div" key={subResourceKey} className="mb-4">
            {({ open }) => (
              <>
                <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left bg-gray-100 rounded-lg hover:bg-gray-200">
                  <span>{title}</span>
                  <ChevronUpIcon className={`${open ? 'transform rotate-180' : ''} w-5 h-5`} />
                </Disclosure.Button>
                <Disclosure.Panel className="mt-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">{title}</h2>
                    <Button variant="blue" size="small" onClick={handleShowSubResourceModal}>
                      New {subResource.model}
                    </Button>
                  </div>
                  {initData.map((subRecord: Record<string, unknown>) => {
                    const handleDuplicate = () => {
                      openModal("generic", {
                        children: (
                          <ResourceDynamicForm
                            campId={campId}
                            model={subResource.model}
                            init={{ ...subRecord, id: undefined } as Record<string, unknown>}
                            onSaved={(record) => {
                              const updatedData = [...initData, record]
                              onSaved({ ...init, [subResourceKey]: updatedData })
                              refetchResource(model)
                              closeModal()
                            }}
                            fields={subResource.fields}
                            parentModel={model}
                          />
                        ),
                        onClose: () => closeModal(),
                        className: "max-w-2xl w-full",
                      })
                    }
                    return (
                      <div key={(subRecord as any).id}>
                        <ResourceDynamicForm
                          campId={campId}
                          model={subResource.model}
                          init={subRecord}
                          onSaved={(record) => {
                            const updatedData = initData.map((r) => (r.id === record.id ? record : r))
                            onSaved({ ...init, [subResourceKey]: updatedData })
                            refetchResource(model)
                          }}
                          fields={subResource.fields}
                          isInline
                          parentModel={model}
                          onDuplicate={handleDuplicate}
                        />
                      </div>
                    )
                  })}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )
      })}
    </div>
  )
}

export default DynamicForm
